import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileCheck2, Landmark, ChevronRight, ShieldCheck, TrendingUp, Truck, Factory, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useFinanceKpis } from '@/hooks/useFinanceKpis';
import { useInvoices } from '@/hooks/useInvoices';
import { useValidationCount, useKpiVentesMois, useKpiAchatsMois, useKpiProductionMois } from '@/hooks/useCommercial';
import { formatMontant } from '@/utils/currency';
import type { Role } from '@/types';

/**
 * Aperçu d'activité de la page d'accueil.
 *
 * L'accueil affichait trois cartes de métadonnées — pays, devise, métier, e-mail,
 * rôle — c'est-à-dire des informations que l'utilisateur connaît déjà et qui sont
 * par ailleurs visibles dans l'en-tête et la barre latérale. Une carte se
 * contentait d'indiquer « Navigation à gauche ».
 *
 * On y met à la place ce qui demande une décision aujourd'hui : créances en
 * retard, proformas en attente, trésorerie. Chaque ligne mène à l'écran où l'on agit.
 *
 * Les blocs sont MONTÉS selon le rôle, et non filtrés après coup : les hooks ne
 * doivent pas appeler des endpoints que le rôle n'a pas le droit d'interroger.
 * Un rôle sans accès aux finances ne voit simplement pas la section — plutôt
 * qu'une erreur sur la première page affichée en arrivant.
 */

const ROLES_KPIS:        Role[] = ['daf', 'dg_filiale', 'admin_holding', 'auditeur'];
const ROLES_FACTURES:    Role[] = ['daf', 'dg_filiale', 'admin_holding', 'auditeur', 'manager', 'commercial', 'acheteur'];
const ROLES_VALIDATIONS: Role[] = ['daf', 'dg_filiale', 'admin_holding'];

/** Une facture représente une créance tant qu'elle n'est ni payée, ni annulée, ni proforma. */
const estCreance = (statut: string) =>
  statut !== 'paid' && statut !== 'cancelled' && statut !== 'proforma';

export const ApercuActivite = () => {
  const role = useAuthStore((s) => s.user?.role) as Role | undefined;

  const voitFactures    = !!role && ROLES_FACTURES.includes(role);
  const voitKpis        = !!role && ROLES_KPIS.includes(role);
  const voitValidations = !!role && ROLES_VALIDATIONS.includes(role);

  if (!voitFactures && !voitKpis && !voitValidations) return null;

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--tx-2)' }}>
        Ce qui demande votre attention
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {voitValidations && <BlocValidations />}
        {voitKpis && <BlocVentesMois />}
        {voitKpis && <BlocAchatsMois />}
        {voitKpis && <BlocProductionMois />}
        {voitFactures && <BlocFactures />}
        {voitKpis && <BlocTresorerie />}
      </div>
    </section>
  );
};

/** Production du mois — lots produits + tonnage + OF (DAF/DG/admin/auditeur). */
const BlocProductionMois = () => {
  const { data, isLoading } = useKpiProductionMois();
  const tonnes = data ? (data.tonnageKg / 1000).toFixed(1) : '0';
  return (
    <Tuile
      icone={Factory}
      libelle="Production ce mois"
      valeur={isLoading ? '…' : data ? `${data.nbLotsProduits} lot${data.nbLotsProduits > 1 ? 's' : ''}` : '—'}
      detail={
        isLoading ? 'Chargement…'
        : data ? `${tonnes} tonnes · ${data.nbOfTermines} OF terminé${data.nbOfTermines > 1 ? 's' : ''} · ${data.nbOfEnCours} en cours`
        : ''
      }
      vers="/operations/production"
    />
  );
};

/** Ventes du mois — commandes validées + montant HT (DAF/DG/admin/auditeur). */
const BlocVentesMois = () => {
  const { data, isLoading } = useKpiVentesMois();
  return (
    <Tuile
      icone={TrendingUp}
      libelle="Ventes ce mois"
      valeur={isLoading ? '…' : data ? `${formatMontant(BigInt(data.montantHtFcfa * 100))} FCFA` : '—'}
      detail={
        isLoading ? 'Chargement…'
        : data ? `${data.nbCommandesValidees} commande${data.nbCommandesValidees > 1 ? 's' : ''} validée${data.nbCommandesValidees > 1 ? 's' : ''} · ${data.nbEnCours} en cours`
        : ''
      }
      vers="/operations/commandes"
    />
  );
};

/** Achats du mois — BCA validés + montant HT engagé (DAF/DG/admin/auditeur). */
const BlocAchatsMois = () => {
  const { data, isLoading } = useKpiAchatsMois();
  return (
    <Tuile
      icone={Truck}
      libelle="Achats ce mois"
      valeur={isLoading ? '…' : data ? `${formatMontant(BigInt(data.montantHtFcfa * 100))} FCFA` : '—'}
      detail={
        isLoading ? 'Chargement…'
        : data ? `${data.nbBcaValides} BCA validé${data.nbBcaValides > 1 ? 's' : ''} · ${data.nbEnCours} en cours`
        : ''
      }
      vers="/operations/achats"
    />
  );
};

/** Validations DAF en attente — toutes les files (TICKET-004 C-04). */
const BlocValidations = () => {
  const { data, isLoading } = useValidationCount();
  const total = data?.total ?? 0;
  const proformas = data?.parFile.proformas ?? 0;
  const autres    = total - proformas;

  return (
    <Tuile
      icone={ShieldCheck}
      libelle="Validations en attente"
      valeur={isLoading ? '…' : total === 0 ? 'Aucune' : `${total} dossier${total > 1 ? 's' : ''}`}
      detail={
        isLoading ? 'Chargement…'
        : total === 0 ? 'Tout est à jour'
        : `${proformas} proforma${proformas > 1 ? 's' : ''} · ${autres} autre${autres > 1 ? 's' : ''} pièce${autres > 1 ? 's' : ''}${data?.indisponibles ? ' · file(s) indisponible(s)' : ''}`
      }
      vers="/validation"
      urgent={!isLoading && total > 0}
    />
  );
};

/** Créances en retard + proformas en attente. Monté pour les rôles habilités. */
const BlocFactures = () => {
  const { data, isLoading } = useInvoices({ limit: 200 });
  const factures = data?.data ?? [];
  const maintenant = new Date();

  const enRetard = factures.filter((f) => {
    if (!estCreance(f.status)) return false;
    if (f.status === 'overdue') return true;
    return !!f.dueDate && new Date(f.dueDate) < maintenant;
  });
  const montantRetard = enRetard.reduce(
    (acc, f) => acc + (BigInt(f.totalCents ?? '0') - BigInt(f.paidAmountCents ?? '0')),
    0n,
  );
  const proformas = factures.filter((f) => f.status === 'proforma');

  return (
    <>
      <Tuile
        icone={AlertTriangle}
        libelle="Créances en retard"
        valeur={isLoading ? '…' : enRetard.length === 0 ? 'Aucune' : `${formatMontant(montantRetard)} FCFA`}
        detail={
          isLoading ? 'Chargement…'
          : enRetard.length === 0 ? "Rien à relancer aujourd'hui"
          : `${enRetard.length} facture${enRetard.length > 1 ? 's' : ''} à relancer`
        }
        vers="/finance/invoices"
        urgent={!isLoading && enRetard.length > 0}
      />
      <Tuile
        icone={FileCheck2}
        libelle="Proformas à traiter"
        valeur={isLoading ? '…' : proformas.length === 0 ? 'Aucune' : String(proformas.length)}
        detail={
          isLoading ? 'Chargement…'
          : proformas.length === 0 ? 'Aucun devis en attente'
          : 'En attente de validation ou de conversion'
        }
        vers="/finance/invoices"
      />
    </>
  );
};

/** Trésorerie du dernier snapshot. Monté pour les rôles habilités aux KPI. */
const BlocTresorerie = () => {
  const { data: kpis, isLoading } = useFinanceKpis();
  return (
    <Tuile
      icone={Landmark}
      libelle="Trésorerie nette"
      valeur={isLoading ? '…' : kpis ? `${formatMontant(kpis.tresorerieCents)} FCFA` : '—'}
      detail="Solde des comptes de trésorerie"
      vers="/finance"
    />
  );
};

const Tuile = ({
  icone: Icone, libelle, valeur, detail, vers, urgent,
}: {
  icone: LucideIcon;
  libelle: string;
  valeur: ReactNode;
  detail: ReactNode;
  vers: string;
  urgent?: boolean;
}) => (
  <Link
    to={vers}
    className="group flex items-center gap-4 rounded-xl border p-4 transition-colors"
    style={{
      borderColor: urgent ? '#FCA5A5' : 'var(--bdr)',
      background:  urgent ? '#FEF6F6' : '#fff',
    }}
  >
    <span
      className="flex size-11 shrink-0 items-center justify-center rounded-lg"
      style={{
        background: urgent ? '#FEE2E2' : 'var(--p-soft)',
        color:      urgent ? '#991B1B' : 'var(--p)',
      }}
    >
      <Icone className="size-5" />
    </span>

    <span className="min-w-0 flex-1">
      <span className="block text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--tx-3)' }}>
        {libelle}
      </span>
      <span className="mt-0.5 block truncate text-lg font-bold" style={{ color: urgent ? '#991B1B' : 'var(--tx)' }}>
        {valeur}
      </span>
      <span className="mt-0.5 block text-xs" style={{ color: 'var(--tx-2)' }}>
        {detail}
      </span>
    </span>

    <ChevronRight
      className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5"
      style={{ color: urgent ? '#991B1B' : 'var(--p)' }}
    />
  </Link>
);
